import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwh204Api } from '../../../services/pwh204Api';
import type { FH140, FH141 } from '../../../services/pwh204Api';

export function usePWH204Record() {
  const [record, setRecord] = useState<FH140 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwh204Api.get(id);
      setRecord(data);
      return data;
    } catch (err: any) {
      message.error('載入失敗：' + (err?.response?.data?.error || err.message));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    mode: 'add' | 'edit',
    master: Partial<FH140>,
    details: Partial<FH141>[]
  ) => {
    setSaving(true);
    try {
      let result: FH140;
      if (mode === 'add') {
        result = await pwh204Api.create(master, details);
      } else {
        result = await pwh204Api.update(master.fh140_01!, master, details);
      }
      setRecord(result);
      message.success('存檔成功');
      return result;
    } catch (err: any) {
      const apiErr = err?.response?.data;
      if (apiErr?.errors) {
        const msgs = (apiErr.errors as Array<{ message: string }>)
          .map(e => e.message)
          .join('；');
        message.error('驗證錯誤：' + msgs);
      } else {
        message.error('存檔失敗：' + (apiErr?.error || err.message));
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    setSaving(true);
    try {
      await pwh204Api.delete(id);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch (err: any) {
      message.error('刪除失敗：' + (err?.response?.data?.error || err.message));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { record, loading, saving, load, save, remove, setRecord };
}
